import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ResponseAPI } from '../models/HttpBase/response-api';
import {
  PwaAuthMeResponse,
  PwaAuthTokenResponse,
  PwaGuestSessionRequest,
  PwaGuestSessionResponse,
  PwaLoginRequest,
  PwaProfileAvatarCatalogItem,
  PwaRefreshRequest,
  PwaRefreshRevokeRequest,
  PwaRegisterRequest,
  PwaUserProfileUpdateRequest
} from '../models/pwa/pwa-auth.dto';
import {
  PwaFavoritoClaimGuestResponse,
  PwaFavoritosClaimGuestRequest
} from '../models/pwa/pwa-favoritos.dto';
import { AuthService } from './auth.service';
import { HttpClientService } from './http-client.service';

@Injectable({
  providedIn: 'root'
})
export class PwaAuthApiService {
  private readonly authBasePath = 'pwa/auth';
  private readonly guestBasePath = 'pwa/guest';
  private readonly favoritosBasePath = 'pwa/favoritos';

  constructor(
    private readonly httpClientService: HttpClientService,
    private readonly authService: AuthService
  ) {}

  public register(request: PwaRegisterRequest): Observable<ResponseAPI<PwaAuthTokenResponse>> {
    return this.httpClientService
      .postAnonymous<PwaAuthTokenResponse>(`${this.authBasePath}/register`, request)
      .pipe(switchMap((response) => this.syncAuthSessionAndClaimGuest(response)));
  }

  public login(request: PwaLoginRequest): Observable<ResponseAPI<PwaAuthTokenResponse>> {
    return this.httpClientService
      .postAnonymous<PwaAuthTokenResponse>(`${this.authBasePath}/login`, request)
      .pipe(switchMap((response) => this.syncAuthSessionAndClaimGuest(response)));
  }

  public refresh(request: PwaRefreshRequest): Observable<ResponseAPI<PwaAuthTokenResponse>> {
    return this.httpClientService
      .postAnonymous<PwaAuthTokenResponse>(`${this.authBasePath}/refresh`, request)
      .pipe(
        map((response) => {
          if (response.Success && response.ResponseObject) {
            this.authService.setSessionFromAuthResponse(response.ResponseObject);
          }
          return response;
        })
      );
  }

  public revokeRefresh(request: PwaRefreshRevokeRequest): Observable<ResponseAPI<boolean>> {
    return this.httpClientService.postAnonymous<boolean>(
      `${this.authBasePath}/refresh/revoke`,
      request
    );
  }

  public logout(request: PwaRefreshRevokeRequest): Observable<ResponseAPI<boolean>> {
    return this.httpClientService.postAnonymous<boolean>(`${this.authBasePath}/logout`, request);
  }

  public updateProfile(request: PwaUserProfileUpdateRequest): Observable<ResponseAPI<boolean>> {
    return this.httpClientService.put<boolean>(`${this.authBasePath}/profile`, request);
  }

  public fetchAvatarCatalog(): Observable<ResponseAPI<PwaProfileAvatarCatalogItem[]>> {
    return this.httpClientService
      .get<unknown>(`${this.authBasePath}/profile/avatars`)
      .pipe(map((response) => this.mapAvatarCatalogResponse(response)));
  }

  public fetchMe(): Observable<ResponseAPI<PwaAuthMeResponse>> {
    return this.httpClientService.get<PwaAuthMeResponse>(`${this.authBasePath}/me`).pipe(
      map((response) => {
        if (response.Success && response.ResponseObject) {
          this.authService.setSessionFromMeResponse(response.ResponseObject);
        }
        return response;
      })
    );
  }

  public ensureGuestSession(): Observable<ResponseAPI<PwaGuestSessionResponse> | null> {
    const existingGuestId = this.authService.getGuestId();
    if (existingGuestId) {
      return of(null);
    }

    return this.upsertGuestSession({
      DeviceKey: this.authService.getOrCreateDeviceKey()
    });
  }

  public upsertGuestSession(
    request: PwaGuestSessionRequest
  ): Observable<ResponseAPI<PwaGuestSessionResponse>> {
    return this.httpClientService
      .postAnonymous<PwaGuestSessionResponse>(`${this.guestBasePath}/session`, request)
      .pipe(
        map((response) => {
          if (response.Success && response.ResponseObject?.GuestID) {
            this.authService.setGuestId(response.ResponseObject.GuestID);
          }
          return response;
        })
      );
  }

  public claimGuestFavorites(
    guestId: string
  ): Observable<ResponseAPI<PwaFavoritoClaimGuestResponse>> {
    const request: PwaFavoritosClaimGuestRequest = { GuestID: guestId };
    return this.httpClientService.post<PwaFavoritoClaimGuestResponse>(
      `${this.favoritosBasePath}/claim-guest`,
      request
    );
  }

  private syncAuthSessionAndClaimGuest(
    response: ResponseAPI<PwaAuthTokenResponse>
  ): Observable<ResponseAPI<PwaAuthTokenResponse>> {
    if (!response.Success || !response.ResponseObject) {
      return of(response);
    }

    this.authService.setSessionFromAuthResponse(response.ResponseObject);

    const guestId = this.authService.getGuestId();
    if (!guestId) {
      return of(response);
    }

    return this.claimGuestFavorites(guestId).pipe(
      map((claimResponse) => {
        if (claimResponse.Success) {
          this.authService.clearGuestId();
        }

        return response;
      }),
      catchError(() => of(response))
    );
  }

  private mapAvatarCatalogResponse(
    response: ResponseAPI<unknown>
  ): ResponseAPI<PwaProfileAvatarCatalogItem[]> {
    const mapped = new ResponseAPI<PwaProfileAvatarCatalogItem[]>();
    mapped.ResponseStatus = response.ResponseStatus;
    mapped.ResponseDescription = response.ResponseDescription;
    mapped.ResponseObject = this.normalizeAvatarCatalogPayload(response.ResponseObject);
    return mapped;
  }

  private normalizeAvatarCatalogPayload(payload: unknown): PwaProfileAvatarCatalogItem[] {
    const source = this.extractAvatarItems(payload);
    const normalized = source
      .map((item, index) => this.mapAvatarCatalogItem(item, index))
      .filter((item): item is PwaProfileAvatarCatalogItem => !!item);

    return normalized.sort((left, right) => {
      const groupDiff = this.resolveAvatarGroupOrder(left.Group) - this.resolveAvatarGroupOrder(right.Group);
      if (groupDiff !== 0) {
        return groupDiff;
      }

      const orderDiff = left.SortOrder - right.SortOrder;
      if (orderDiff !== 0) {
        return orderDiff;
      }

      return left.Code.localeCompare(right.Code);
    });
  }

  private extractAvatarItems(payload: unknown): unknown[] {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const raw = payload as Record<string, unknown>;
    const candidateKeys = [
      'Items',
      'items',
      'Avatars',
      'avatars',
      'Catalogo',
      'catalogo',
      'Data',
      'data'
    ];

    for (const key of candidateKeys) {
      const candidate = raw[key];
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private mapAvatarCatalogItem(
    source: unknown,
    index: number
  ): PwaProfileAvatarCatalogItem | null {
    if (!source || typeof source !== 'object') {
      return null;
    }

    const raw = source as Record<string, unknown>;
    const code = this.readText(raw, ['AvatarCode', 'avatarCode', 'Code', 'code', 'Codigo', 'codigo']).toUpperCase();
    const avatarUrl = this.readText(raw, [
      'AvatarUrl',
      'avatarUrl',
      'Url',
      'url',
      'ImageUrl',
      'imageUrl',
      'ImagenUrl',
      'imagenUrl'
    ]);

    if (!code || !avatarUrl) {
      return null;
    }

    const label =
      this.readText(raw, ['Label', 'label', 'Nombre', 'nombre', 'DisplayName', 'displayName']) ||
      code;

    return {
      Code: code,
      AvatarUrl: avatarUrl,
      Label: label,
      Group: this.readAvatarGroup(raw, code),
      SortOrder: this.readAvatarSortOrder(raw, code, index)
    };
  }

  private readAvatarGroup(
    source: Record<string, unknown>,
    code: string
  ): 'M' | 'F' | '' {
    const rawGroup = this.readText(source, [
      'Group',
      'group',
      'Gender',
      'gender',
      'Genero',
      'genero',
      'Sexo',
      'sexo'
    ]).toUpperCase();
    if (rawGroup.startsWith('M')) {
      return 'M';
    }
    if (rawGroup.startsWith('F')) {
      return 'F';
    }

    if (code.startsWith('M')) {
      return 'M';
    }
    if (code.startsWith('F')) {
      return 'F';
    }

    return '';
  }

  private readAvatarSortOrder(
    source: Record<string, unknown>,
    code: string,
    index: number
  ): number {
    const explicitOrder = Number(
      source['SortOrder'] ??
        source['sortOrder'] ??
        source['Orden'] ??
        source['orden'] ??
        source['Index'] ??
        source['index']
    );
    if (Number.isFinite(explicitOrder) && explicitOrder > 0) {
      return Math.trunc(explicitOrder);
    }

    const match = /^([A-Z])(\d+)$/.exec(code);
    if (match) {
      return Number(match[2]);
    }

    return index + 1;
  }

  private resolveAvatarGroupOrder(group: string): number {
    if (group === 'M') {
      return 1;
    }
    if (group === 'F') {
      return 2;
    }
    return 3;
  }

  private readText(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const candidate = source[key];
      if (typeof candidate === 'string') {
        const normalized = candidate.trim();
        if (normalized) {
          return normalized;
        }
      }
    }

    return '';
  }
}
